/**
 * Component: sa-mobile-nav-metaobject
 * Description: Mobile navigation using Metaobjects with horizontal scrollable first level and expandable second/third levels
 */

if (!customElements.get('sa-mobile-nav-metaobject')) {
  class SaMobileNavMetaobject extends HTMLElement {
    constructor() {
      super();
      this.handleFirstLevelClick = this.handleFirstLevelClick.bind(this);
      this.handleSecondLevelClick = this.handleSecondLevelClick.bind(this);
      this.handleThirdLevelLinkClick = this.handleThirdLevelLinkClick.bind(this);
      this.updateAnnouncementOffset = this.updateAnnouncementOffset.bind(this);
    }

    connectedCallback() {
      this.firstLevelItems = this.querySelectorAll('.sa-mobile-nav-metaobject__first-level-item');
      this.panels = this.querySelectorAll('.sa-mobile-nav-metaobject__panel');
      this.secondLevelItems = this.querySelectorAll('.sa-mobile-nav-metaobject__second-level-item');
      this.thirdLevelPanels = this.querySelectorAll('.sa-mobile-nav-metaobject__third-level-panel');
      this.secondLevelOnlyLinks = this.querySelectorAll('.sa-mobile-nav-metaobject__second-level-only-link');
      this.noSubmenuLinks = this.querySelectorAll('.sa-mobile-nav-metaobject__no-submenu-link');
      
      this.updateVerticalLinePosition();

      if (this.firstLevelItems.length > 0) {
        this.firstLevelItems.forEach(item => {
          item.addEventListener('click', this.handleFirstLevelClick);
        });
      }

      if (this.secondLevelItems.length > 0) {
        this.secondLevelItems.forEach(item => {
          const button = item.querySelector('.sa-mobile-nav-metaobject__second-level-button');
          if (button) {
            button.addEventListener('click', this.handleSecondLevelClick);
          }
        });
      }

      if (this.thirdLevelPanels.length > 0) {
        this.thirdLevelPanels.forEach(panel => {
          const links = panel.querySelectorAll('.sa-mobile-nav-metaobject__third-level-link');
          links.forEach(link => {
            link.addEventListener('click', this.handleThirdLevelLinkClick);
          });
        });
      }

      if (this.secondLevelOnlyLinks.length > 0) {
        this.secondLevelOnlyLinks.forEach(link => {
          link.addEventListener('click', () => {
            if (window.theme && window.theme.NavDrawer) {
              window.theme.NavDrawer.close();
            }
          });
        });
      }

      if (this.noSubmenuLinks.length > 0) {
        this.noSubmenuLinks.forEach(link => {
          link.addEventListener('click', () => {
            if (window.theme && window.theme.NavDrawer) {
              window.theme.NavDrawer.close();
            }
          });
        });
      }

      this.initHorizontalScroll();

      this.updateAnnouncementOffset();
      setTimeout(this.updateAnnouncementOffset, 100);
      window.addEventListener('resize', this.updateAnnouncementOffset);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.updateAnnouncementOffset);
      if (this.firstLevelItems.length > 0) {
        this.firstLevelItems.forEach(item => {
          item.removeEventListener('click', this.handleFirstLevelClick);
        });
      }

      if (this.secondLevelItems.length > 0) {
        this.secondLevelItems.forEach(item => {
          const button = item.querySelector('.sa-mobile-nav-metaobject__second-level-button');
          if (button) {
            button.removeEventListener('click', this.handleSecondLevelClick);
          }
        });
      }

      if (this.thirdLevelPanels.length > 0) {
        this.thirdLevelPanels.forEach(panel => {
          const links = panel.querySelectorAll('.sa-mobile-nav-metaobject__third-level-link');
          links.forEach(link => {
            link.removeEventListener('click', this.handleThirdLevelLinkClick);
          });
        });
      }
    }

    handleFirstLevelClick(e) {
      e.preventDefault();
      const clickedItem = e.currentTarget;
      const navIndex = clickedItem.getAttribute('data-nav-index');

      this.firstLevelItems.forEach(item => {
        item.classList.remove('sa-mobile-nav-metaobject__first-level-item--active');
      });
      clickedItem.classList.add('sa-mobile-nav-metaobject__first-level-item--active');

      this.panels.forEach(panel => {
        panel.classList.remove('sa-mobile-nav-metaobject__panel--active');
      });

      const targetPanel = this.querySelector(`.sa-mobile-nav-metaobject__panel[data-nav-index="${navIndex}"]`);
      if (targetPanel) {
        targetPanel.classList.add('sa-mobile-nav-metaobject__panel--active');
      }
    }

    handleSecondLevelClick(e) {
      e.preventDefault();
      const clickedButton = e.currentTarget;
      const clickedItem = clickedButton.closest('.sa-mobile-nav-metaobject__second-level-item');
      const secondIndex = clickedItem.getAttribute('data-second-index');
      const currentPanel = clickedItem.closest('.sa-mobile-nav-metaobject__panel');
      
      if (!currentPanel) return;

      const thirdLevelPanel = clickedItem.querySelector(`.sa-mobile-nav-metaobject__third-level-panel[data-second-index="${secondIndex}"]`);
      
      if (thirdLevelPanel) {
        const isActive = clickedItem.classList.contains('sa-mobile-nav-metaobject__second-level-item--active');
        
        if (isActive) {
          clickedItem.classList.remove('sa-mobile-nav-metaobject__second-level-item--active');
          thirdLevelPanel.classList.remove('sa-mobile-nav-metaobject__third-level-panel--active');
        } else {
          this.secondLevelItems.forEach(item => {
            if (item.closest('.sa-mobile-nav-metaobject__panel') === currentPanel) {
              item.classList.remove('sa-mobile-nav-metaobject__second-level-item--active');
              const panel = item.querySelector('.sa-mobile-nav-metaobject__third-level-panel');
              if (panel) {
                panel.classList.remove('sa-mobile-nav-metaobject__third-level-panel--active');
              }
            }
          });
          
          clickedItem.classList.add('sa-mobile-nav-metaobject__second-level-item--active');
          thirdLevelPanel.classList.add('sa-mobile-nav-metaobject__third-level-panel--active');
          
          setTimeout(() => {
            this.updateVerticalLinePosition();
          }, 100);
        }
      }
    }

    handleThirdLevelLinkClick(e) {
      if (window.theme && window.theme.NavDrawer) {
        window.theme.NavDrawer.close();
      }
    }

    activateSecondLevelItem(item) {
      const currentPanel = item.closest('.sa-mobile-nav-metaobject__panel');
      if (!currentPanel) return;

      this.secondLevelItems.forEach(secondItem => {
        if (secondItem.closest('.sa-mobile-nav-metaobject__panel') === currentPanel) {
          secondItem.classList.remove('sa-mobile-nav-metaobject__second-level-item--active');
          const panel = secondItem.querySelector('.sa-mobile-nav-metaobject__third-level-panel');
          if (panel) {
            panel.classList.remove('sa-mobile-nav-metaobject__third-level-panel--active');
          }
        }
      });

      item.classList.add('sa-mobile-nav-metaobject__second-level-item--active');

      const secondIndex = item.getAttribute('data-second-index');
      const thirdLevelPanel = item.querySelector(`.sa-mobile-nav-metaobject__third-level-panel[data-second-index="${secondIndex}"]`);
      
      if (thirdLevelPanel) {
        thirdLevelPanel.classList.add('sa-mobile-nav-metaobject__third-level-panel--active');
        setTimeout(() => {
          this.updateVerticalLinePosition();
        }, 100);
      }
    }

    initHorizontalScroll() {
      const firstLevelWrapper = this.querySelector('.sa-mobile-nav-metaobject__first-level-wrapper');
      if (!firstLevelWrapper) return;

      const activeItem = this.querySelector('.sa-mobile-nav-metaobject__first-level-item--active');
      if (activeItem) {
        const scrollLeft = activeItem.offsetLeft - (firstLevelWrapper.offsetWidth / 2) + (activeItem.offsetWidth / 2);
        const firstLevel = this.querySelector('.sa-mobile-nav-metaobject__first-level');
        if (firstLevel) {
          firstLevel.scrollTo({
            left: Math.max(0, scrollLeft),
            behavior: 'smooth'
          });
        }
      }
    }

    updateVerticalLinePosition() {
      const thirdLevelContents = this.querySelectorAll('.sa-mobile-nav-metaobject__third-level-content');
      thirdLevelContents.forEach(content => {
        const firstLabel = content.querySelector('.sa-mobile-nav-metaobject__third-level-label');
        if (firstLabel) {
          const labelWidth = firstLabel.offsetWidth;
          content.style.setProperty('--label-width', `${labelWidth}px`);
        }
      });
    }

    updateAnnouncementOffset() {
      try {
        const bar = document.querySelector('.announcement-bar');
        let h = 0;
        if (bar) {
          const rect = bar.getBoundingClientRect();
          h = Math.max(0, Math.round(rect.height || bar.offsetHeight || 0));
        }
        document.documentElement.style.setProperty('--announcement-bar-height', `${h}px`);
      } catch (e) {}
    }
  }

  customElements.define('sa-mobile-nav-metaobject', SaMobileNavMetaobject);
}
